import { useState, useEffect, useRef } from "react";
import { Props_SnippetVideo } from "./Video.props";
import { Legacy_CompileMarkdown } from "../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler";
import SnippetLink from "../Shared/Link/Link";

type Video = "youtube" | "vimeo" | "video" | null;

const setAutoplay = (url: string) => {
    const hasAutoplay = url.includes("autoplay=1");
    if (!hasAutoplay) {
        const separator = url.includes("?") ? "&" : "?";

        const youtubeWatchScript =
            url.includes("youtube") && !url.includes("embed");
        if (youtubeWatchScript) {
            const videoIdMatch = url.match(
                /(?:v=|\/)([0-9A-Za-z_-]{11})(?:&|$)/
            );
            const videoId = videoIdMatch ? videoIdMatch[1] : null;
            if (videoId) {
                return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
            }
        }
        return `${url}${separator}autoplay=1`;
    }
};

const getVideoMimeType = (url: string): string => {
    const extension = url.split(".").pop()?.toLowerCase();
    switch (extension) {
        case "mp4":
            return "video/mp4";
        case "webm":
            return "video/webm";
        case "ogg":
        case "ogv":
            return "video/ogg";
        case "mov":
            return "video/quicktime";
        default:
            return "video/mp4";
    }
};

const getVideoType = (url: string): Video => {
    if (!url) return null;
    if (url.includes("youtube") || url.includes("youtu.be")) return "youtube";
    if (url.includes("vimeo")) return "vimeo";
    return "video";
};

const SnippetVideo = (props: Props_SnippetVideo) => {
    const { heading, content, repeatable_link, video, linkProps, style } =
        props;

    const [videoType, setVideoType] = useState<Video>(null);
    const [playing, setPlaying] = useState<boolean>(false);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const classHeadingVisibility: string = heading.visibility?.value
        ? `snippet-video__heading--${heading.visibility.value}`
        : ``;
    const classHeadingAlignment: string = heading.alignment?.value
        ? `snippet-video__heading--alignment-${heading.alignment.value}`
        : ``;
    const classContentPosition: string = content.position?.value
        ? `snippet-video--position-${content.position.value}`
        : ``;
    const classContentAlignment: string = content.alignment?.value
        ? `snippet-video--alignment-${content.alignment.value}`
        : ``;
    const classVideoPlaying: string = playing ? `snippet-video--playing` : ``;
    const classSnippetStyle: string = style?.layoutstyle?.value
        ? `snippet-video--${style.layoutstyle.value}`
        : ``;
    const classLinkAlignment: string = linkProps.alignment?.value
        ? `snippet-video__container-links--${linkProps.alignment.value}`
        : ``;
    const classdisplayContent: string = content.visible?.value
        ? `snippet-video--content-visible`
        : `snippet-video--content-hidden`;

    useEffect(() => {
        setVideoType(video?.url?.value ? getVideoType(video.url.value) : null);
    }, [playing, videoType]);

    return (
        <section
            className={`snippet-video ${classVideoPlaying} ${classSnippetStyle} ${classContentPosition} ${classContentAlignment} ${classdisplayContent}`}
        >
            <div className="snippet-video__container">
                <div className="snippet-video__container-content">
                    <h2
                        className={`snippet-video__heading ${classHeadingVisibility} ${classHeadingAlignment}`}
                    >
                        {heading?.content?.value}
                    </h2>
                    <div className={`snippet-video__content`}>
                        {content.content?.value &&
                            Legacy_CompileMarkdown(content?.content?.value)}
                    </div>

                    <div
                        className={`snippet-video__container-links ${classLinkAlignment}`}
                    >
                        {repeatable_link?.map((link, index) => (
                            <SnippetLink
                                key={`link-${index}`}
                                url={link.linkHref?.value ?? ""}
                                className="snippet-video__link"
                                linkStyles={link.linkStyle?.value}
                                openInNewTab={link.linkTarget?.value === "true"}
                                rel={
                                    link.linkTarget?.value === "true"
                                        ? "noopener noreferrer"
                                        : undefined
                                }
                            >
                                {link.linkText?.value ?? ""}
                            </SnippetLink>
                        ))}
                    </div>
                </div>

                <div className="snippet-video__container-video-player">
                    {video?.thumbnail?.value && (
                        <figure className="snippet-video__container-thumbnail">
                            <img
                                src={video.thumbnail.value}
                                alt={video?.alt?.value || ""}
                                className="snippet-video__thumbnail"
                                loading="lazy"
                                width="1280"
                                height="720"
                            />
                        </figure>
                    )}
                    {video?.url?.value && (
                        <div className="snippet-video__container-video">
                            {video?.thumbnail?.value && (
                                <div className="snippet-video__container-button">
                                    <button
                                        className="snippet-video__button-play"
                                        aria-label={`{{js.snippet.video.button.play.aria-label}}`}
                                        onClick={() => setPlaying(true)}
                                    ></button>

                                    {video.buttonText?.value && (
                                        <p className="snippet-video__button-text">
                                            {video.buttonText?.value}
                                        </p>
                                    )}
                                </div>
                            )}
                            {playing &&
                                (videoType === "youtube" ||
                                    videoType === "vimeo") && (
                                    <iframe
                                        className="snippet-video__video"
                                        src={setAutoplay(video.url.value)}
                                        width="1280"
                                        height="720"
                                        title="Video player"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        referrerPolicy={
                                            videoType === "youtube"
                                                ? "strict-origin-when-cross-origin"
                                                : undefined
                                        }
                                        allowFullScreen
                                    ></iframe>
                                )}
                            {playing && videoType === "video" && (
                                <video
                                    className="snippet-video__video"
                                    controls
                                    controlsList="nodownload"
                                    width="1280"
                                    height="720"
                                    ref={videoRef}
                                    autoPlay
                                >
                                    <track kind="captions" />
                                    <source
                                        src={video.url.value}
                                        type={getVideoMimeType(video.url.value)}
                                    />
                                    Your browser does not support the video tag.
                                </video>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default SnippetVideo;
